/**
 * sanitizeEmailHtml.js — the ONE place customer HTML becomes renderable.
 *
 * WHY THIS EXISTS
 * ---------------
 * `rma_inbound_emails.body_html` is whatever an arbitrary sender put in a
 * mail to the RMA mailbox. This app serves the SPA from the same origin and
 * keeps the Supabase JWT in localStorage, so one surviving script is a
 * session theft, not a cosmetic bug. Every consumer goes through this module
 * — there is no second sanitizer config anywhere in the repo, on purpose:
 * two configs drift, and the weaker one becomes the hole.
 *
 * THE MODEL (plan §Sicherheitsmodell 1–7)
 * ---------------------------------------
 * 1. A pure ALLOWLIST of tags and attributes. Not `USE_PROFILES` — it
 *    collides with `ALLOWED_TAGS` and quietly widens the surface.
 * 2. Links, belt and suspenders, two INDEPENDENT mechanisms:
 *      (a) `ALLOWED_URI_REGEXP` refuses anything that is not literally
 *          `https:`, `http:` or `mailto:` — which also refuses RELATIVE and
 *          PROTOCOL-RELATIVE URLs. DOMPurify's default regexp permits both,
 *          and both are dangerous here: `/rma/1/delete` resolves against our
 *          own origin, `//evil.tld` inherits our scheme.
 *      (b) `hardenAnchors` re-checks the surviving href after URL
 *          normalisation and forces `target="_blank"
 *          rel="noopener noreferrer"` (anti-tabnabbing), overwriting
 *          whatever the sender supplied.
 * 3. No `<img>` at all ⇒ no remote fetches ⇒ no tracking pixels, no IP leak
 *    to the sender when an operator opens a case.
 * 4. The render container is the caller's job (`contain: content`, own font)
 *    — see `EmailHtmlBody` in ActivityEntryRow.jsx.
 * 5. Fallback chain, BOTH directions: unusable input → `null`; and an OUTPUT
 *    with no visible content → `null` as well, so an image-only mail routes
 *    the caller to the text path instead of painting an empty bubble.
 * 6. WHEN the HTML path may be taken is not decided here — see
 *    `toActivityEntry.js` (V2 conversation only, `body_html` column only,
 *    never on a `split_segments` fan-out).
 * 7. SANITIZE-LAST. DOMPurify is the last transformation before render. The
 *    quote split therefore runs on a `RETURN_DOM_FRAGMENT` DOM — never on
 *    the serialized string — and each resulting fragment is sanitized ONE
 *    MORE TIME on its way out. Splitting strings after sanitising is exactly
 *    how a mutation-XSS payload gets re-imported through the second parse.
 *    The observable form of this rule, pinned in the tests: every string this
 *    module returns is a DOMPurify fixpoint.
 *
 * WHAT THIS MODULE DOES NOT COVER
 * -------------------------------
 * Attachments. An operator clicking a mailed `.html` file never touches this
 * code — that perimeter is `backend/files_routes.py` (nosniff + forced
 * download outside the raster allowlist). And there is still NO CSP on the
 * app origin: `object-src 'none'; base-uri 'none'`, later Trusted Types, is
 * a named follow-up and a live residual risk. See
 * `docs/knowledge/design-language/pattern-library.md` §"Kunden-HTML rendern".
 */
import DOMPurify from 'dompurify';

import { isQuoteMarkerLine } from './splitQuotedThread';

// ── Configuration ─────────────────────────────────────────────────────────────

/**
 * Tag allowlist — the formatting vocabulary real mail actually uses.
 * Absent on purpose: img, svg, math, style, form/input, iframe, object,
 * embed, base, meta, link, script.
 */
const ALLOWED_TAGS = [
  'p', 'br', 'div', 'span', 'a', 'b', 'strong', 'i', 'em', 'u',
  'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tr', 'td', 'th', 'hr',
];

/**
 * Attribute allowlist.
 *
 * `target` and `rel` are in here even though a SENDER's copy of them is
 * worthless to us: `hardenAnchors` overwrites both before the final pass, and
 * the final pass has to be allowed to keep what we just wrote — that is what
 * makes rule 7 (sanitize LAST) achievable without a post-sanitize DOM edit.
 * Neither attribute carries a script surface, on an anchor or anywhere else.
 *
 * `class`, `id`, `name`, `style`, `data-*` and `aria-*` are all absent —
 * `id`/`name` are the DOM-clobbering vector, `style` the CSS one, and
 * `ALLOW_DATA_ATTR` / `ALLOW_ARIA_ATTR` default to TRUE in DOMPurify, so
 * they must be switched off explicitly rather than merely left out.
 */
const ALLOWED_ATTR = ['href', 'title', 'colspan', 'rowspan', 'target', 'rel'];

/** Schemes an anchor may keep. Anything else — including no scheme — loses the href. */
const ALLOWED_URI_REGEXP = /^(?:https?|mailto):/i;

/** Same list, post-normalisation, for the second (independent) check. */
const ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

const PURIFY_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOWED_URI_REGEXP,
  // NOT cosmetic. DOMPurify runs ALLOWED_URI_REGEXP against the value of
  // EVERY attribute that is not marked URI-safe — not just the URL-carrying
  // ones. `title` is URI-safe by default; `colspan="2"` / `target="_blank"` /
  // `rel="…"` are not, so a regexp this strict silently deletes them (tables
  // lose their spans, links lose the anti-tabnabbing pair). Marking them
  // URI-safe skips the URL test for attributes that are not URLs. `href`
  // stays OUT of this list — it is the one that must be tested.
  ADD_URI_SAFE_ATTR: ['colspan', 'rowspan', 'target', 'rel'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  // Redundant next to a pure allowlist, and kept anyway: it makes the
  // intent greppable, and it survives an accidental widening of ALLOWED_TAGS.
  FORBID_TAGS: ['style', 'script', 'svg', 'math', 'form', 'input', 'button',
    'iframe', 'object', 'embed', 'base', 'meta', 'link', 'img'],
  FORBID_ATTR: ['style', 'srcset', 'src', 'background', 'formaction', 'xlink:href'],
  WHOLE_DOCUMENT: false,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_TRUSTED_TYPE: false,
};

const FRAGMENT_CONFIG = { ...PURIFY_CONFIG, RETURN_DOM_FRAGMENT: true };

// ── Internals ─────────────────────────────────────────────────────────────────

/**
 * Force every surviving anchor into the safe shape, and strip the href of
 * any that slipped past `ALLOWED_URI_REGEXP` after URL normalisation.
 *
 * Runs on the DOM fragment, i.e. BEFORE the final sanitize — see rule 7.
 *
 * @param {DocumentFragment|Element} root
 */
function hardenAnchors(root) {
  for (const a of root.querySelectorAll('a')) {
    const href = (a.getAttribute('href') || '').trim();
    let ok = ALLOWED_URI_REGEXP.test(href);
    if (ok) {
      // Second, independent check: normalise, then judge the protocol. A
      // value can satisfy the string test and still normalise to something
      // else once the URL parser has had it.
      try {
        ok = ALLOWED_PROTOCOLS.has(new URL(href).protocol);
      } catch {
        ok = false;
      }
    }
    if (!ok) {
      // Keep the element (its TEXT is the customer's words) but make it inert.
      a.removeAttribute('href');
      a.removeAttribute('target');
      a.removeAttribute('rel');
      continue;
    }
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  }
}

/** Serialize a list of already-sanitized nodes back to markup. */
function serialize(nodes) {
  const host = document.createElement('div');
  for (const node of nodes) host.appendChild(node);
  return host.innerHTML;
}

/**
 * True when a sanitized markup string would paint nothing.
 *
 * Text extraction runs through DOMPurify with an EMPTY allowlist rather than
 * through `innerHTML` + `textContent`, so no code path in this module ever
 * assigns untrusted markup to a live DOM node.
 */
function hasVisibleContent(html) {
  if (!html) return false;
  const text = DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  return typeof text === 'string' && text.trim().length > 0;
}

/**
 * The FINAL pass. Every exit from this module goes through it, so "DOMPurify
 * ran last" is a property of the module, not of each call site.
 *
 * @returns {string|null} sanitized markup, or null when nothing visible is left.
 */
function finalize(html) {
  let clean;
  try {
    clean = DOMPurify.sanitize(html, PURIFY_CONFIG);
  } catch {
    return null;
  }
  if (typeof clean !== 'string') clean = String(clean ?? '');
  return hasVisibleContent(clean) ? clean : null;
}

/** Sanitize to a DOM fragment with anchors already hardened, or null. */
function toHardenedFragment(html) {
  if (typeof html !== 'string' || !html.trim()) return null;
  let fragment;
  try {
    fragment = DOMPurify.sanitize(html, FRAGMENT_CONFIG);
  } catch {
    return null;
  }
  if (!fragment || typeof fragment.querySelectorAll !== 'function') return null;
  hardenAnchors(fragment);
  return fragment;
}

/** Element children of a node, ignoring whitespace-only text nodes. */
function elementChildren(node) {
  return [...node.childNodes].filter(
    (n) => n.nodeType === 1 || (n.nodeType === 3 && n.textContent.trim()),
  );
}

/**
 * Containers a client wraps a whole mail in, which carry no meaning of their
 * own once `class` and `style` are gone. `p`/`blockquote` are NOT here: a
 * one-paragraph mail and a whole-mail quote both mean something.
 */
const UNWRAPPABLE = new Set(['DIV', 'SPAN', 'BODY']);

/**
 * Peel off single-child layout wrappers before looking for the boundary.
 *
 * [R15 B5, 2026-08-04] The first cut searched `fragment.childNodes` and so
 * assumed a FLAT document. No real client sends one — Outlook puts the entire
 * mail inside one `<div class="WordSection1">`, so the fragment had exactly
 * one child, that child was not a boundary, and every Outlook thread came
 * back unsplit with the whole quote wall in the body. Capped at four levels:
 * this is a heuristic, and an unbounded loop over hostile input is not.
 */
function unwrapContainers(fragment) {
  let nodes = elementChildren(fragment);
  for (let depth = 0; depth < 4; depth += 1) {
    if (nodes.length !== 1) break;
    const only = nodes[0];
    if (only.nodeType !== 1 || !UNWRAPPABLE.has(only.tagName)) break;
    const inner = elementChildren(only);
    if (inner.length === 0) break;
    nodes = inner;
  }
  return nodes;
}

/**
 * True when a node starts the quoted thread.
 *
 * Three shapes, all of them observed in real mail:
 *
 *   1. the node IS a `<blockquote>` — Apple Mail, and the tail of most others;
 *   2. the node's own text is a reply/forward marker line — Outlook, whose
 *      quote header ("Von: … Gesendet: … Betreff: …") is a plain block;
 *   3. the node WRAPS the quote — Gmail's `div.gmail_quote`, which holds the
 *      "Am … schrieb …:" attribution in its first child and the quoted text
 *      in a nested blockquote.
 *
 * Class names are deliberately not consulted: `class` is not in the attribute
 * allowlist, so `gmail_quote` never survives sanitization to be matched. That
 * is why the boundary is read from STRUCTURE plus the marker vocabulary in
 * `splitQuotedThread.js`, which the plain-text path shares.
 */
function isQuoteBoundary(node) {
  if (!node || node.nodeType !== 1) return false;
  if (node.tagName === 'BLOCKQUOTE') return true;
  if (isQuoteMarkerLine((node.textContent || '').trim())) return true;

  // Shape 3. Requires an actual nested quote, so an ordinary paragraph that
  // merely starts with "Am Montag …" cannot trigger it — a false boundary
  // hides the customer's own words, which is worse than a missed split.
  if (!node.querySelector || !node.querySelector('blockquote')) return false;
  const first = elementChildren(node)[0];
  if (!first || first.nodeType !== 1) return false;
  if (first.tagName === 'BLOCKQUOTE') return true;
  return isQuoteMarkerLine((first.textContent || '').trim());
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Sanitize an email HTML body for rendering.
 *
 * @param {unknown} html  Raw `body_html` — attacker-controlled.
 * @returns {string|null} Safe markup, or null when the caller must fall back
 *   to the plain-text path (unusable input, sanitizer failure, or an output
 *   with nothing visible in it).
 */
export function sanitizeEmailHtml(html) {
  const fragment = toHardenedFragment(html);
  if (!fragment) return null;
  return finalize(serialize([...fragment.childNodes]));
}

/**
 * Sanitize AND split an email HTML body into reply + quoted thread.
 *
 * The split runs on the sanitized DOM (rule 7); both halves get a final
 * sanitize on the way out. Never returns an empty reply: a mail that is
 * entirely a quote comes back whole in `bodyHtml`, exactly as the plain-text
 * splitter behaves.
 *
 * @param {unknown} html
 * @returns {{ bodyHtml: string|null, quotedHtml: string|null }}
 */
export function splitSanitizedEmailHtml(html) {
  const fragment = toHardenedFragment(html);
  if (!fragment) return { bodyHtml: null, quotedHtml: null };

  const nodes = unwrapContainers(fragment);
  const cut = nodes.findIndex(isQuoteBoundary);
  if (cut < 0) return { bodyHtml: finalize(serialize(nodes)), quotedHtml: null };

  const head = nodes.slice(0, cut);
  const tail = nodes.slice(cut);
  // `serialize` MOVES nodes, so head must be read before tail is touched.
  const bodyHtml = finalize(serialize(head));
  const quotedHtml = finalize(serialize(tail));

  // Shortening a bubble is the goal; emptying one is a regression.
  if (!bodyHtml) return { bodyHtml: quotedHtml, quotedHtml: null };
  return { bodyHtml, quotedHtml };
}
